import {
  Card,
  CardSelectionType,
  CheckboxInput,
  FilterList,
  Input,
  SelectableCard,
  Tooltip,
  useMounted,
  useToast,
  breakpoints,
  icons,
  sizing,
} from '@chillibean/component-library';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';

import { RouteLeavingGuard } from '../../components/container';
import { FooterButtons } from '../../components/container/FooterButtons';
import { assignStudyProtocolSites, fetchSites, setProtocolUpdated } from '../../redux/actions';
import { SitesAccordion } from './components/presentational';

export function SitesPage() {
  // HOOKS
  // React
  const [initialSelectedSites, setInitialSelectedSites] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [selectedAll, setSelectedAll] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [usedSites, setUsedSites] = useState([]);
  const [availableSites, setAvailableSites] = useState([]);
  // Router
  const { study_protocol_id: studyProtocolId } = useParams();
  const { url } = useRouteMatch();
  // Store
  const dispatch = useDispatch();
  const { account } = useSelector((state) => state.internalReducer.internal);
  const { data: studyProtocol } = useSelector((state) => state.protocolReducer.protocol);
  const { data: protocolUpdated } = useSelector((state) => state.protocolReducer.protocolUpdated);
  const { loading: sitesAssignmentLoading } = useSelector((state) => state.protocolReducer.sitesAssignment);
  const { data: sites, loading } = useSelector((state) => state.sitesReducer.sites);
  // Custom
  const { mounted } = useMounted({ requiredValues: [account, studyProtocol] });
  const { showErrorToast, showSuccessToast } = useToast();
  // Variables
  const unLockedTooltip = 'In order to assign to a site, the trial configuration must be locked.';
  const selectAllDisabled = loading || !availableSites.length || !studyProtocol?.locked;

  useEffect(() => {
    if (mounted) {
      dispatch(fetchSites());
    }
  }, [mounted]);

  useEffect(() => {
    if (sites && studyProtocol) {
      const newUsedSites = sites.filter((site) => site.studyProtocol && site.studyProtocol.id !== studyProtocol.id);
      const newAvailableSites = sites.filter(
        (site) => !site.studyProtocol || site.studyProtocol.id === studyProtocol.id,
      );
      const newSelectedSites = newAvailableSites
        .filter((site) => site.studyProtocol?.id === studyProtocol.id)
        .map((site) => site.id);
      const newSelectedAll = Boolean(newAvailableSites.length) && newAvailableSites.length === newSelectedSites.length;

      setUsedSites(newUsedSites);
      setAvailableSites(newAvailableSites);
      setSelectedSites(newSelectedSites);
      setInitialSelectedSites(newSelectedSites);
      setSelectedAll(newSelectedAll);
    }
  }, [sites, studyProtocol]);

  useEffect(() => {
    const isDirty = initialSelectedSites.length !== selectedSites.length;
    dispatch(setProtocolUpdated({ data: isDirty }));
  }, [initialSelectedSites, selectedSites]);

  useEffect(() => {
    setSelectedAll(Boolean(availableSites.length) && availableSites.length === selectedSites.length);
  }, [selectedSites]);

  const searchHandler = (event) => {
    setSearchValue(event.target.value);
  };

  const onSelectAll = () => {
    if (selectedAll) {
      setSelectedSites([]);
    } else {
      setSelectedSites([...availableSites.map((site) => site.id)]);
    }

    setSelectedAll(!selectedAll);
  };

  const onSelect = (site) => {
    if (selectedSites.includes(site.id)) {
      setSelectedSites(selectedSites.filter((selectedSite) => selectedSite !== site.id));
    } else {
      setSelectedSites([...selectedSites, site.id]);
    }
  };

  const isSelected = (site) => {
    return selectedSites.includes(site.id);
  };

  const updateSites = () => {
    dispatch(
      assignStudyProtocolSites({
        data: {
          sites: selectedSites,
        },
        identifier: studyProtocol.id,
        onError: showErrorToast,
        onSuccess: () => showSuccessToast('Site assignments saved successfully'),
      }),
    );
    setInitialSelectedSites(selectedSites);
  };

  const formButtons = () => {
    return [
      {
        handleClick: updateSites,
        isDisabled: loading || sitesAssignmentLoading || !studyProtocol?.locked,
        loading: sitesAssignmentLoading,
        name: 'Save Trial Configuration',
      },
    ];
  };
  const backButton = {
    isDisabled: loading || sitesAssignmentLoading,
    name: 'Back',
  };

  return (
    <>
      <RouteLeavingGuard
        headerText="You have unsaved changes"
        navigationBlocked={protocolUpdated}
        warningText="You are about to discard all the data you have entered. This action is irreversible."
      />
      <Container>
        {Boolean(usedSites.length) && (
          <SitesAccordion
            baseUrl={url.replace(`/${studyProtocolId}/sites`, '')}
            numberOfAllSites={sites.length}
            sites={usedSites}
          />
        )}
        <Controls>
          <Tooltip content={selectAllDisabled ? unLockedTooltip : null} id="select-all-checkbox">
            <CheckboxInput
              checked={selectedAll}
              disabled={selectAllDisabled}
              label="Select all"
              onChange={onSelectAll}
              value={1}
            />
          </Tooltip>
          <Input
            disabled={loading || !availableSites.length}
            icon={icons.baselineSearch}
            label="Search Available Sites"
            onChange={searchHandler}
            value={searchValue}
          />
        </Controls>
        {loading && <Card loading={loading} />}
        {!loading && Boolean(availableSites.length) && (
          <Cards>
            <FilterList filterProperty="title" filterValue={searchValue}>
              {availableSites.map((site) => (
                <SelectableCard
                  key={site.id}
                  details={site.getDetails()}
                  disabled={!studyProtocol?.locked}
                  onClick={() => onSelect(site)}
                  selected={isSelected(site)}
                  title={site.name}
                  tooltip={unLockedTooltip}
                  type={CardSelectionType.MULTI}
                  value={site.id}
                />
              ))}
            </FilterList>
          </Cards>
        )}
        {!loading && !availableSites.length && <Card disabled>No available sites</Card>}
        <FooterButtons backButton={backButton} buttons={formButtons()} />
      </Container>
    </>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizing.xlarge};

  padding-bottom: ${sizing.xllarge};

  // ! override default CP styles
  input[type='checkbox'] {
    margin: 0;
  }
`;

const Controls = styled.div`
  display: grid;
  grid-template-columns: auto 350px;
  justify-content: space-between;
  align-items: center;
  gap: ${sizing.medium};
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${sizing.medium};

  // ! override default CP styles
  input[type='checkbox'] {
    margin: 0;
  }

  @media (width <= ${breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

export default SitesPage;
